'use client';

import { useEffect, useState } from 'react';
import { Timer } from 'lucide-react';
import { formatCountdown, msUntilDeparture } from '@/lib/tours/urgent-departure';

interface Props {
  tourDate: string;
  startTime: string | null | undefined;
  english?: boolean;
  /** Se llama una vez cuando el temporizador llega a cero. */
  onExpire?: () => void;
}

/** Aviso compacto: «Salida urgente · sale en 1 h 42 min». La cuenta se pinta
 *  solo tras montar: si llega renderizado del servidor (fecha en la URL), el
 *  texto no puede diferir entre servidor y navegador. */
export default function UrgentDepartureNotice({ tourDate, startTime, english = false, onExpire }: Props) {
  const [nowMs, setNowMs] = useState<number | null>(null);
  useEffect(() => {
    setNowMs(Date.now());
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const ms = nowMs === null ? null : msUntilDeparture(tourDate, startTime, new Date(nowMs));
  const expired = ms === 0;
  useEffect(() => {
    if (expired) onExpire?.();
  }, [expired, onExpire]);
  if (nowMs !== null && ms === null) return null;
  return (
    <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <Timer className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden="true" />
      <p>
        <strong className="font-semibold">{english ? 'Urgent departure' : 'Salida urgente'}</strong>
        {' · '}
        {expired ? (
          english ? 'this departure has left' : 'esta salida ya partió'
        ) : (
          <span role="timer" aria-live="off">
            {english ? 'leaves in ' : 'sale en '}
            <span className="font-semibold tabular-nums">{ms === null ? '…' : formatCountdown(ms)}</span>
          </span>
        )}
      </p>
    </div>
  );
}
